import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { AxiosError } from 'axios';

import initApi from '@/api/initApiV2';
import {
  JobsApi,
  type JobExpanded,
  type JobPaginated,
  type JobsApiCreateJobRequest,
  type JobsApiGetJobExpandedRequest,
  type JobsApiGetJobRunExpandedRequest,
  type JobsApiGetJobRunsRequest,
  type JobsApiGetJobsRequest,
  type JobsApiSetJobEnabledRequest,
  type JobsApiStopJobRunRequest,
  type JobsApiTriggerJobRunRequest,
  type JobsApiUpdateJobRequest,
  type Run,
  type RunPaginated,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request.ts';

export const useJobsStore = defineStore('jobs', () => {
  const jobsData = ref<JobPaginated | null>(null);
  const currentJob = ref<JobExpanded | null>(null);
  const runsData = ref<RunPaginated | null>(null);
  const runs = computed<Run[]>(() => runsData.value?.items ?? []);

  async function fetchJobs(params: JobsApiGetJobsRequest, options?: RequestConfig) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.getJobs(params, options);
      if (!response) return;

      jobsData.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function getJobExpanded(params: JobsApiGetJobExpandedRequest, options?: RequestConfig) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.getJobExpanded(params, options);
      currentJob.value = response?.data ?? null;
      return currentJob.value;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function fetchRuns(params: JobsApiGetJobRunsRequest, options?: RequestConfig) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.getJobRuns(params, options);
      if (!response) return;

      runsData.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function getRunExpanded(params: JobsApiGetJobRunExpandedRequest) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.getJobRunExpanded(params);
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function createJob(params: JobsApiCreateJobRequest) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.createJob(params);
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function triggerJobRun(params: JobsApiTriggerJobRunRequest) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.triggerJobRun(params);
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function setJobEnabled(params: JobsApiSetJobEnabledRequest) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.setJobEnabled(params);
      if (response?.data && currentJob.value?.id === response.data.id) {
        // Merge the updated Job on top so the expanded `latest_run` / `steps` survive.
        currentJob.value = { ...currentJob.value, ...response.data };
      }
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function updateJob(params: JobsApiUpdateJobRequest) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.updateJob(params);
      if (response?.data && currentJob.value?.id === response.data.id) {
        // Merge the updated Job on top so the expanded `latest_run` / `steps` survive.
        currentJob.value = { ...currentJob.value, ...response.data };
      }
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function stopJobRun(params: JobsApiStopJobRunRequest) {
    try {
      const jobsApi = await initApi(JobsApi);
      const response = await jobsApi?.stopJobRun(params);
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  function reset() {
    jobsData.value = null;
    currentJob.value = null;
    runsData.value = null;
  }

  return {
    jobsData,
    currentJob,
    runsData,
    runs,
    fetchJobs,
    getJobExpanded,
    fetchRuns,
    getRunExpanded,
    createJob,
    triggerJobRun,
    setJobEnabled,
    updateJob,
    stopJobRun,
    reset,
  };
});
